import React from 'react'
import { Box, styled } from '@mui/material'
import { useSelector } from 'react-redux'
import MaterialSymbol from '../../../../components/MaterialSymbol'
import Typography from '../../../../components/Typography'
import { colors } from '../../../../themes/colors'
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage'

const FlexRow = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
})

const FlexColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
})

export enum StockAvailabilityCodes {
  IN_STOCK = 'AVAIL',
  LOW_STOCK = 'LOW',
  OUT_OF_STOCK = 'OOS',
  URGENT_UNAVAILABLE = 'URGENT',
  SEE_MORE_OPTIONS = 'MORE',
}

export type StockAvailabilityStatusType = {
  code: StockAvailabilityCodes | undefined
  label: string | undefined
  quantity: number | undefined
}

const mapping = {
  [StockAvailabilityCodes.IN_STOCK]: {
    icon: 'check',
    color: colors.primary500,
  },
  [StockAvailabilityCodes.LOW_STOCK]: {
    icon: 'production_quantity_limits',
    color: colors.grey700,
  },
  [StockAvailabilityCodes.OUT_OF_STOCK]: {
    icon: 'remove_shopping_cart',
    color: colors.grey700,
  },
  [StockAvailabilityCodes.URGENT_UNAVAILABLE]: {
    icon: 'deployed_code_alert',
    color: colors.grey700,
  },
  [StockAvailabilityCodes.SEE_MORE_OPTIONS]: {
    icon: 'info',
    color: colors.grey700,
  },
}

const StockAvailabilityStatusUI = ({
  color,
  icon,
  backordersAvailable,
  label,
}: {
  color: string
  icon: string
  backordersAvailable: boolean
  label: string
}) => {
  return (
    <FlexRow>
      <MaterialSymbol sx={{ color: color }}>{icon}</MaterialSymbol>
      <FlexColumn>
        <Typography variant='body2' sx={{ color: color }}>
          {label}
        </Typography>
        {backordersAvailable && (
          <Typography variant='body2' sx={{ color: color }}>
            backorders available
          </Typography>
        )}
      </FlexColumn>
    </FlexRow>
  )
}

export const StockAvailabilityStatus = ({
  status,
  isBackOrderAllowed,
}: {
  status: StockAvailabilityStatusType
  isBackOrderAllowed?: boolean
}) => {
  const currentAccount = useSelector(selectCurrentFarmerAccount)
  const isTfo = currentAccount?.hasTfoAccess

  if (status.code && status.label) {
    let label = status.label
    let { icon, color } = mapping[status.code] ?? mapping[StockAvailabilityCodes.LOW_STOCK]

    if (isTfo && status.quantity !== undefined) {
      label = `${status.quantity.toFixed(0)} in stock`
      if (status.quantity > 0) {
        icon = mapping[StockAvailabilityCodes.IN_STOCK].icon
        color = mapping[StockAvailabilityCodes.IN_STOCK].color
      } else {
        icon = mapping[StockAvailabilityCodes.OUT_OF_STOCK].icon
        color = mapping[StockAvailabilityCodes.OUT_OF_STOCK].color
      }
    }

    return (
      <StockAvailabilityStatusUI
        color={color}
        icon={icon}
        backordersAvailable={
          !!isBackOrderAllowed && status.code === StockAvailabilityCodes.OUT_OF_STOCK
        }
        label={label}
      />
    )
  }

  // No code or label meant it is not available so display zero availability
  if (isTfo) {
    const { icon, color } = mapping[StockAvailabilityCodes.OUT_OF_STOCK]
    return (
      <StockAvailabilityStatusUI
        color={color}
        icon={icon}
        backordersAvailable={!!isBackOrderAllowed}
        label='Out of stock'
      />
    )
  }

  return null
}
